import { useState, useEffect, useRef, useMemo } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { getDateRangeForDaysBack, getProgressInsightsFirestoreQueryRange } from "../../../lib/dateUtils";
import { normalizeFirestoreDate } from "../../../lib/firestoreDate";
import {
  flattenWorkoutToDataPoints,
  aggregateByExerciseAndDate,
  workoutsCompleted as countWorkoutsCompleted,
  workoutsPerWeek,
  totalVolume as computeTotalVolume,
  consecutiveWeeksWithWorkouts,
  mostImprovedExercise,
  personalRecordsInPeriod,
  weeklyVolumeSeries,
  averageWeeklyVolume,
  exercisesWithProgression,
} from "../lib/progressInsightsUtils";

const DEFAULT_DAYS = 90;

/**
 * Fetch reportExercise for the last N days (completed only) and derive progress insights.
 * Returns workouts, flattened data points, aggregated by (exercise, date), and metrics.
 *
 * @param {{ clientId: string, organizationId: string, days?: number }} options
 * @returns {{ workouts, dataPoints, aggregated, metrics, loading, error }}
 */
export function useProgressInsights({ clientId, organizationId, days = DEFAULT_DAYS }) {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const isValid = Boolean(clientId && organizationId);
  /** Display window: last `days` (effective workout date). */
  const displayRange = useMemo(
    () => (isValid ? getDateRangeForDaysBack(days) : { start: null, end: null }),
    [isValid, days]
  );
  /** Wide Firestore window on `completedOn` so we fetch enough docs before client-side rolling-window filter. */
  const { start, end } = useMemo(
    () => (isValid ? getProgressInsightsFirestoreQueryRange(days) : { start: null, end: null }),
    [isValid, days]
  );

  useEffect(() => {
    if (!isValid || !start || !end || !displayRange.start || !displayRange.end) {
      setWorkouts([]);
      setLoading(true); // Keep loading until we have valid params (e.g. auth ready)
      setError(null);
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    setLoading(true);
    setError(null);
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("reportExercise")
      .where("status", "==", "complete")
      .where("completedOn", ">=", start)
      .where("completedOn", "<=", end)
      .orderBy("completedOn", "desc");

    const winStart = displayRange.start;
    const winEnd = displayRange.end;

    unsubscribeRef.current = ref.onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            createdOn: normalizeFirestoreDate(d.createdOn),
            completedOn: normalizeFirestoreDate(d.completedOn),
          };
        });
        const filtered = list.filter((w) => {
          const t = normalizeFirestoreDate(w.completedOn);
          if (!t) return false;
          return t >= winStart && t <= winEnd;
        });
        setWorkouts(filtered);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setWorkouts([]);
        setLoading(false);
      }
    );
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [clientId, organizationId, start, end, displayRange.start, displayRange.end, isValid]);

  const { dataPoints, aggregated, metrics } = useMemo(() => {
    if (!Array.isArray(workouts) || workouts.length === 0) {
      return {
        dataPoints: [],
        aggregated: [],
        metrics: {
          workoutsCompleted: 0,
          workoutsPerWeek: { avg: 0, perWeek: [] },
          totalVolume: 0,
          avgWeeklyVolumeLoad: 0,
          consecutiveWeeks: 0,
          mostImproved: null,
          personalRecords: [],
          weeklyVolumeSeries: [],
          exercisesWithProgression: [],
        },
      };
    }
    const dataPoints = workouts.flatMap(flattenWorkoutToDataPoints);
    const aggregated = aggregateByExerciseAndDate(workouts);
    const volSeries = weeklyVolumeSeries(workouts, dataPoints);
    const tv = computeTotalVolume(dataPoints);
    const metrics = {
      workoutsCompleted: countWorkoutsCompleted(workouts),
      workoutsPerWeek: workoutsPerWeek(workouts),
      totalVolume: tv,
      avgWeeklyVolumeLoad: averageWeeklyVolume(tv, volSeries),
      consecutiveWeeks: consecutiveWeeksWithWorkouts(workouts),
      mostImproved: mostImprovedExercise(aggregated),
      personalRecords: personalRecordsInPeriod(aggregated),
      weeklyVolumeSeries: volSeries,
      exercisesWithProgression: exercisesWithProgression(aggregated, 2),
    };
    return { dataPoints, aggregated, metrics };
  }, [workouts]);

  return {
    workouts,
    dataPoints,
    aggregated,
    metrics,
    loading,
    error,
  };
}
